import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PersianDatePicker from '../components/PersianDatePicker';
import AppointmentCallHistoryModal from '../components/admin/AppointmentCallHistoryModal';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessMonitoring } from '../utils/monitoringAccess';
import moment from 'moment-jalaali';

interface MonitoringStats {
  total_appointments: number;
  pending_appointments: number;
  incomplete_appointments: number;
  completed_appointments: number;
  in_progress_appointments: number;
  cancelled_appointments: number;
  today_appointments: number;
  total_users: number;
  latest_appointments: Appointment[];
}

interface Appointment {
  id: number;
  user_id: number;
  service_id?: number;
  status: string;
  step_completed: number;
  appointment_date?: string;
  appointment_time?: string;
  tracking_code?: string;
  cancel_reason?: string;
  cancelled_by_username?: string;
  created_at: string;
  updated_at?: string;
  user?: {
    full_name: string;
    phone: string;
  };
  service?: {
    name: string;
  };
}

interface ActivityLog {
  id: number;
  admin_id: number;
  admin_username: string;
  activity_type: string;
  entity_type: string;
  entity_id: number | null;
  description: string;
  old_value: any;
  new_value: any;
  ip_address: string | null;
  created_at: string;
}

interface UserActivityStats {
  admin_id: number;
  admin_username: string;
  total_activities: number;
  unique_appointments: number;
}

type MonitoringStatusFilter = 'incomplete' | 'pending' | 'completed' | 'cancelled';

const STATUS_LIST_PAGE_SIZE = 50;
const MODAL_LIST_PAGE_SIZE = 50;

const STATUS_CARD_CONFIG: Record<
  MonitoringStatusFilter,
  { title: string; valueKey: keyof MonitoringStats; color: string; iconBg: string; icon: React.ReactNode }
> = {
  incomplete: {
    title: 'در انتظار',
    valueKey: 'incomplete_appointments',
    color: 'text-yellow-600',
    iconBg: 'bg-yellow-100',
    icon: (
      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  pending: {
    title: 'در حال انجام',
    valueKey: 'pending_appointments',
    color: 'text-blue-600',
    iconBg: 'bg-blue-100',
    icon: (
      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  completed: {
    title: 'تکمیل شده',
    valueKey: 'completed_appointments',
    color: 'text-green-600',
    iconBg: 'bg-green-100',
    icon: (
      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  cancelled: {
    title: 'لغو شده',
    valueKey: 'cancelled_appointments',
    color: 'text-red-600',
    iconBg: 'bg-red-100',
    icon: (
      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
  },
};

const AdminMonitoring: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);
  const [userActivityStats, setUserActivityStats] = useState<UserActivityStats[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(moment().format('jYYYY/jMM/jDD'));
  const selectedDateRef = useRef<string>(moment().format('jYYYY/jMM/jDD'));
  const [selectedUserLogs, setSelectedUserLogs] = useState<ActivityLog[]>([]);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [showUserLogsModal, setShowUserLogsModal] = useState(false);
  const [userLogsLoading, setUserLogsLoading] = useState(false);
  const [showStatusListModal, setShowStatusListModal] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<MonitoringStatusFilter | null>(null);
  const [statusListAppointments, setStatusListAppointments] = useState<Appointment[]>([]);
  const [statusListLoading, setStatusListLoading] = useState(false);
  const [statusListPage, setStatusListPage] = useState(1);
  const [statusListTotal, setStatusListTotal] = useState(0);
  const [showAppointmentDetailModal, setShowAppointmentDetailModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [appointmentDetailLoading, setAppointmentDetailLoading] = useState(false);
  const [showLatestAppointmentsModal, setShowLatestAppointmentsModal] = useState(false);
  const [latestAppointmentsList, setLatestAppointmentsList] = useState<Appointment[]>([]);
  const [showCallHistoryModal, setShowCallHistoryModal] = useState(false);
  const [callHistoryAppointment, setCallHistoryAppointment] = useState<Appointment | null>(null);
  const [latestAppointmentsLoading, setLatestAppointmentsLoading] = useState(false);
  const [latestAppointmentsPage, setLatestAppointmentsPage] = useState(1);
  const [showActivityLogModal, setShowActivityLogModal] = useState(false);
  const [activityLogModalList, setActivityLogModalList] = useState<ActivityLog[]>([]);
  const [activityLogModalLoading, setActivityLogModalLoading] = useState(false);
  const [activityLogModalPage, setActivityLogModalPage] = useState(1);
  const [activityLogModalHasMore, setActivityLogModalHasMore] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastAppointmentIdRef = useRef<number | null>(null);
  const lastStatsRef = useRef<MonitoringStats | null>(null);
  const lastLogIdRef = useRef<number | null>(null);

  const fetchActivityLogs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/activity-logs?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const logs: ActivityLog[] = await response.json();

        // اگر اولین بار است، تمام لاگ‌ها را اضافه می‌کنیم
        if (lastLogIdRef.current === null && logs.length > 0) {
          lastLogIdRef.current = logs[0].id;
          setActivityLog(logs);
        } else if (logs.length > 0) {
          // فقط لاگ‌های جدید را اضافه می‌کنیم
          const newLogs = logs.filter(log => log.id > lastLogIdRef.current!);
          if (newLogs.length > 0) {
            // فیلتر کردن لاگ‌های تکراری بر اساس ID
            setActivityLog(prev => {
              const existingIds = new Set(prev.map(log => log.id));
              const uniqueNewLogs = newLogs.filter(log => !existingIds.has(log.id));
              return [...uniqueNewLogs, ...prev].slice(0, 100); // نگه‌داری 100 تا آخر
            });
            lastLogIdRef.current = logs[0].id;
          }
        }
      }
    } catch (err) {
      // خطا در دریافت لاگ‌ها را نادیده می‌گیریم
    }
  };

  const normalizeDateForActivityStats = (date: string) => {
    // PersianDatePicker outputs jYYYY/jMM/jDD; activity stats endpoint expects YYYY-MM-DD.
    if (date.includes('/')) {
      const parts = date.split('/');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        const jalaliDate = moment().jYear(year).jMonth(month).jDate(day);
        if (jalaliDate.isValid()) {
          return jalaliDate.format('YYYY-MM-DD');
        }
      }
    }
    return date;
  };

  const fetchUserActivityStats = async (date?: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const apiBaseUrl = getApiBaseUrl();
      const dateParam = normalizeDateForActivityStats(date || selectedDate);
      const url = `${apiBaseUrl}/admin/activity-logs/stats${dateParam ? `?date=${dateParam}` : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const stats: UserActivityStats[] = await response.json();
        setUserActivityStats(stats);
      }
    } catch (err) {
      // خطا در دریافت آمار را نادیده می‌گیریم
    }
  };

  const fetchStatusAppointments = async (filter: MonitoringStatusFilter, page: number = 1) => {
    try {
      setStatusListLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const skip = (page - 1) * STATUS_LIST_PAGE_SIZE;
      const response = await fetch(
        `${apiBaseUrl}/admin/appointments?skip=${skip}&limit=${STATUS_LIST_PAGE_SIZE}&list_filter=${filter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت لیست نوبت‌ها');
      }

      const data: Appointment[] = await response.json();
      const totalHeader = response.headers.get('X-Total-Count');
      const parsed = totalHeader != null && totalHeader !== '' ? parseInt(totalHeader, 10) : NaN;
      const total = Number.isFinite(parsed) ? parsed : data.length;

      setStatusListAppointments(data);
      setStatusListTotal(total);
      setStatusListPage(page);
    } catch {
      setStatusListAppointments([]);
      setStatusListTotal(0);
    } finally {
      setStatusListLoading(false);
    }
  };

  const openStatusListModal = (filter: MonitoringStatusFilter) => {
    setSelectedStatusFilter(filter);
    setShowStatusListModal(true);
    setStatusListPage(1);
    fetchStatusAppointments(filter, 1);
  };

  const closeStatusListModal = () => {
    setShowStatusListModal(false);
    setSelectedStatusFilter(null);
    setStatusListAppointments([]);
    setStatusListTotal(0);
    setStatusListPage(1);
  };

  const openAppointmentDetail = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentDetailLoading(false);
    setShowAppointmentDetailModal(true);
  };

  const openAppointmentFromLog = async (log: ActivityLog) => {
    if (log.entity_type !== 'appointment' || !log.entity_id || log.entity_id <= 0) {
      return;
    }

    setShowAppointmentDetailModal(true);
    setAppointmentDetailLoading(true);
    setSelectedAppointment(null);

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${log.entity_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت جزئیات نوبت');
      }

      const apt: Appointment = await response.json();
      if (!apt.cancelled_by_username?.trim() && getActivityKind(log) === 'cancelled') {
        apt.cancelled_by_username = log.admin_username;
      }
      if (!apt.cancel_reason?.trim() && getActivityKind(log) === 'cancelled') {
        const reason = extractCancelReason(log);
        if (reason) {
          apt.cancel_reason = reason;
        }
      }
      setSelectedAppointment(apt);
    } catch {
      setShowAppointmentDetailModal(false);
      setSelectedAppointment(null);
    } finally {
      setAppointmentDetailLoading(false);
    }
  };

  const closeAppointmentDetailModal = () => {
    setShowAppointmentDetailModal(false);
    setSelectedAppointment(null);
    setAppointmentDetailLoading(false);
  };

  const openCallHistoryModal = (appointment: Appointment) => {
    setCallHistoryAppointment(appointment);
    setShowCallHistoryModal(true);
  };

  const closeCallHistoryModal = () => {
    setShowCallHistoryModal(false);
    setCallHistoryAppointment(null);
  };

  const handleViewFullAppointment = (id: number) => {
    closeAppointmentDetailModal();
    closeStatusListModal();
    setShowLatestAppointmentsModal(false);
    setShowActivityLogModal(false);
    navigate(`/admin/appointments/${id}`);
  };

  const fetchLatestAppointmentsModal = async (page: number = 1) => {
    try {
      setLatestAppointmentsLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const skip = (page - 1) * MODAL_LIST_PAGE_SIZE;
      const response = await fetch(
        `${apiBaseUrl}/admin/appointments?skip=${skip}&limit=${MODAL_LIST_PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت نوبت‌ها');
      }

      const data: Appointment[] = await response.json();
      setLatestAppointmentsList(data);
      setLatestAppointmentsPage(page);
    } catch {
      setLatestAppointmentsList([]);
    } finally {
      setLatestAppointmentsLoading(false);
    }
  };

  const openLatestAppointmentsModal = () => {
    setShowLatestAppointmentsModal(true);
    setLatestAppointmentsPage(1);
    fetchLatestAppointmentsModal(1);
  };

  const closeLatestAppointmentsModal = () => {
    setShowLatestAppointmentsModal(false);
    setLatestAppointmentsList([]);
    setLatestAppointmentsPage(1);
  };

  const fetchActivityLogsModal = async (page: number = 1) => {
    try {
      setActivityLogModalLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const skip = (page - 1) * MODAL_LIST_PAGE_SIZE;
      const response = await fetch(
        `${apiBaseUrl}/admin/activity-logs?skip=${skip}&limit=${MODAL_LIST_PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('خطا در دریافت لاگ فعالیت‌ها');
      }

      const data: ActivityLog[] = await response.json();
      setActivityLogModalList(data);
      setActivityLogModalPage(page);
      setActivityLogModalHasMore(data.length === MODAL_LIST_PAGE_SIZE);
    } catch {
      setActivityLogModalList([]);
      setActivityLogModalHasMore(false);
    } finally {
      setActivityLogModalLoading(false);
    }
  };

  const openActivityLogModal = () => {
    setShowActivityLogModal(true);
    setActivityLogModalPage(1);
    fetchActivityLogsModal(1);
  };

  const closeActivityLogModal = () => {
    setShowActivityLogModal(false);
    setActivityLogModalList([]);
    setActivityLogModalPage(1);
    setActivityLogModalHasMore(false);
  };

  const fetchUserDailyLogs = async (adminId: number, adminUsername: string) => {
    try {
      setUserLogsLoading(true);
      setSelectedUserName(adminUsername);
      setShowUserLogsModal(true);

      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/activity-logs?limit=200&admin_id=${adminId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const logs: ActivityLog[] = await response.json();
        const normalizedDate = normalizeDateForActivityStats(selectedDateRef.current);
        const filtered = logs.filter((log) => {
          if (!log.created_at) return false;
          return log.created_at.slice(0, 10) === normalizedDate;
        });
        setSelectedUserLogs(filtered);
      } else {
        setSelectedUserLogs([]);
      }
    } catch (err) {
      setSelectedUserLogs([]);
    } finally {
      setUserLogsLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/monitoring/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت اطلاعات مانیتورینگ');
      }

      const data = await response.json();

      // تشخیص تغییرات و اضافه کردن به activity log
      if (lastStatsRef.current && data.latest_appointments && data.latest_appointments.length > 0) {
        const latestAppointment = data.latest_appointments[0];

        // بررسی نوبت جدید
        if (lastAppointmentIdRef.current === null) {
          lastAppointmentIdRef.current = latestAppointment.id;
        } else if (latestAppointment.id > lastAppointmentIdRef.current) {
          lastAppointmentIdRef.current = latestAppointment.id;
        }
      } else if (data.latest_appointments && data.latest_appointments.length > 0) {
        // اولین بار - تنظیم lastAppointmentIdRef
        lastAppointmentIdRef.current = data.latest_appointments[0].id;
      }

      setStats(data);
      lastStatsRef.current = data;
      // دریافت لاگ‌های فعالیت
      await fetchActivityLogs();

      // دریافت آمار فعالیت کاربران (با تاریخ انتخاب شده از ref)
      await fetchUserActivityStats(selectedDateRef.current);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');

    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (!canAccessMonitoring()) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }

    // بارگذاری اولیه
    fetchMonitoringData();

    // شروع polling هر 2 ثانیه
    pollingIntervalRef.current = setInterval(() => {
      fetchMonitoringData();
    }, 2000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [navigate]);

  const getAppointmentDisplayStatus = (apt: Appointment) => {
    if (apt.status === 'cancelled') return 'cancelled';
    if (apt.status === 'completed') return 'completed';
    if (apt.status === 'in_progress' && apt.step_completed === 2) return 'in_progress';
    if (apt.step_completed < 2) return 'pending';
    return apt.status;
  };

  const statusListTotalPages = Math.max(1, Math.ceil(statusListTotal / STATUS_LIST_PAGE_SIZE));

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { bg: string; text: string; label: string } } = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'در انتظار' },
      'in_progress': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'در حال انجام' },
      'completed': { bg: 'bg-green-100', text: 'text-green-800', label: 'تکمیل شده' },
      'cancelled': { bg: 'bg-red-100', text: 'text-red-800', label: 'لغو شده' }
    };
    const badge = badges[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getActivityKind = (log: ActivityLog) => {
    const normalizedType = (log.activity_type || '').toLowerCase();
    const normalizedDescription = (log.description || '').toLowerCase();
    const oldValue = JSON.stringify(log.old_value ?? {}).toLowerCase();
    const newValue = JSON.stringify(log.new_value ?? {}).toLowerCase();
    const combined = `${normalizedType} ${normalizedDescription} ${oldValue} ${newValue}`;

    if (combined.includes('cancelled') || combined.includes('لغو')) {
      return 'cancelled';
    }
    if (combined.includes('completed') || combined.includes('تکمیل')) {
      return 'completed';
    }
    if (combined.includes('time_set')) {
      return 'time_set';
    }
    if (combined.includes('status_changed')) {
      return 'status_changed';
    }
    return 'other';
  };

  const getActivityIcon = (log: ActivityLog) => {
    const kind = getActivityKind(log);

    if (kind === 'completed') {
      return (
        <div className="flex-shrink-0 w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    if (kind === 'status_changed') {
      return (
        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      );
    } else if (kind === 'time_set') {
      return (
        <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    } else if (kind === 'cancelled') {
      return (
        <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }
  };

  const getActivityTypeBadge = (log: ActivityLog) => {
    const kind = getActivityKind(log);

    if (kind === 'completed') {
      return { label: 'تکمیل شد', className: 'bg-green-100 text-green-700' };
    }
    if (kind === 'cancelled') {
      return { label: 'لغو نوبت', className: 'bg-red-100 text-red-700' };
    }
    if (kind === 'status_changed') {
      return { label: 'تغییر وضعیت', className: 'bg-blue-100 text-blue-700' };
    }
    if (kind === 'time_set') {
      return { label: 'تنظیم زمان', className: 'bg-purple-100 text-purple-700' };
    }
    return { label: 'سایر فعالیت‌ها', className: 'bg-gray-100 text-gray-700' };
  };

  const getActivityActionText = (log: ActivityLog) => {
    const kind = getActivityKind(log);
    if (kind === 'completed') return 'تکمیل شد';
    if (kind === 'cancelled') return 'لغو شد';
    if (kind === 'time_set') return 'زمان‌بندی شد';
    if (kind === 'status_changed') return 'تغییر وضعیت';
    return 'به‌روزرسانی شد';
  };

  const extractCancelReason = (log: ActivityLog) => {
    const newValueReason = log.new_value?.cancel_reason;
    if (typeof newValueReason === 'string' && newValueReason.trim()) {
      return newValueReason.trim();
    }

    const oldValueReason = log.old_value?.cancel_reason;
    if (typeof oldValueReason === 'string' && oldValueReason.trim()) {
      return oldValueReason.trim();
    }

    const description = log.description || '';
    const reasonMatch =
      description.match(/دلیل لغو[:：]\s*([^-\\n]+)/) ||
      description.match(/cancel reason[:：]\s*([^-\\n]+)/i) ||
      description.match(/دلیل[:：]\s*([^-\\n]+)/);

    if (reasonMatch?.[1]) {
      return reasonMatch[1].trim();
    }

    return '';
  };

  const extractUserNameFromDescription = (description?: string) => {
    if (!description) return 'نامشخص';
    const userMatch = description.match(/کاربر:\s*([^-\n]+)/);
    if (userMatch?.[1]) return userMatch[1].trim();
    return 'نامشخص';
  };

  const extractServiceNameFromDescription = (description?: string) => {
    if (!description) return '';
    const serviceMatch = description.match(/خدمت:\s*([^-\n]+)/);
    return serviceMatch?.[1]?.trim() || '';
  };

  const statusLabelFa = (status?: string) => {
    switch (status) {
      case 'pending':
        return 'در انتظار';
      case 'in_progress':
        return 'در حال انجام';
      case 'completed':
        return 'تکمیل شده';
      case 'cancelled':
        return 'لغو شده';
      default:
        return status || '-';
    }
  };

  const formatLogDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('fa-IR', {
      timeZone: 'Asia/Tehran',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return '-';

    const eventTime = moment(dateString);
    if (!eventTime.isValid()) return '-';

    const now = moment();
    const diffSeconds = now.diff(eventTime, 'seconds');
    if (diffSeconds < 60) return 'لحظاتی قبل';

    const diffMinutes = now.diff(eventTime, 'minutes');
    if (diffMinutes < 60) return `${diffMinutes.toLocaleString('fa-IR')} دقیقه قبل`;

    const diffHours = now.diff(eventTime, 'hours');
    if (diffHours < 24) return `${diffHours.toLocaleString('fa-IR')} ساعت قبل`;

    const diffDays = now.diff(eventTime, 'days');
    if (diffDays < 30) return `${diffDays.toLocaleString('fa-IR')} روز قبل`;

    const diffMonths = now.diff(eventTime, 'months');
    if (diffMonths < 12) return `${diffMonths.toLocaleString('fa-IR')} ماه قبل`;

    const diffYears = now.diff(eventTime, 'years');
    return `${diffYears.toLocaleString('fa-IR')} سال قبل`;
  };

  const latestAppointmentsTotalPages = stats
    ? Math.max(1, Math.ceil(stats.total_appointments / MODAL_LIST_PAGE_SIZE))
    : 1;

  const renderLatestAppointmentItem = (apt: Appointment) => (
    <button
      key={apt.id}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openAppointmentDetail(apt);
      }}
      className="w-full flex items-start justify-between gap-3 p-4 bg-white border border-gray-100 rounded-xl hover:border-blue-200 hover:shadow-sm transition-all text-right"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className="font-bold text-gray-900 truncate">
            {apt.user?.full_name || 'کاربر'}
          </p>
          <p className="text-xs text-gray-500 whitespace-nowrap">
            {formatRelativeTime(apt.created_at).replace('قبل', 'پیش')}
          </p>
        </div>
        <p className="text-sm text-gray-700 mb-2">
          {apt.service?.name || 'نامشخص'}
        </p>
        <p className="text-xs text-gray-500">
          {new Date(apt.created_at).toLocaleString('fa-IR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
      <div className="text-left shrink-0">
        <p className="text-sm font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
          نوبت #{apt.tracking_code || 1000 + apt.id}
        </p>
      </div>
    </button>
  );

  const renderActivityLogItem = (log: ActivityLog) => {
    const activityTypeBadge = getActivityTypeBadge(log);
    const actionText = getActivityActionText(log);
    const cancelReason = actionText === 'لغو شد' ? extractCancelReason(log) : '';
    const isAppointmentLog = log.entity_type === 'appointment' && !!log.entity_id && log.entity_id > 0;

    const content = (
      <>
        {getActivityIcon(log)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">{log.admin_username}</p>
              <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${activityTypeBadge.className}`}>
                {activityTypeBadge.label}
              </span>
            </div>
            <p className="text-xs text-gray-500 whitespace-nowrap">{formatRelativeTime(log.created_at)}</p>
          </div>
          <div className="space-y-1.5 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
            <p className="font-semibold text-gray-900">{actionText}</p>
            <p>
              نام مراجعه‌کننده:{' '}
              <span className="font-medium text-gray-900">{extractUserNameFromDescription(log.description)}</span>
            </p>
            <p>
              نوبت:{' '}
              <span className="font-medium text-blue-700">
                #{log.entity_id && log.entity_id > 0 ? 1000 + log.entity_id : '-'}
              </span>
            </p>
            {cancelReason && (
              <p>
                دلیل لغو: <span className="font-medium text-red-700">{cancelReason}</span>
              </p>
            )}
          </div>
        </div>
      </>
    );

    if (!isAppointmentLog) {
      return (
        <div key={log.id} className="relative">
          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100">
            {content}
          </div>
        </div>
      );
    }

    return (
      <button
        key={log.id}
        type="button"
        onClick={() => openAppointmentFromLog(log)}
        className="w-full relative text-right"
      >
        <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer">
          {content}
        </div>
      </button>
    );
  };

  const renderUserDailyLogItem = (log: ActivityLog) => {
    const activityTypeBadge = getActivityTypeBadge(log);
    const actionText = getActivityActionText(log);
    const kind = getActivityKind(log);
    const cancelReason = kind === 'cancelled' ? extractCancelReason(log) : '';
    const serviceName = extractServiceNameFromDescription(log.description);
    const patientName = extractUserNameFromDescription(log.description);
    const isAppointmentLog = log.entity_type === 'appointment' && !!log.entity_id && log.entity_id > 0;
    const oldStatus = log.old_value?.status as string | undefined;
    const newStatus = log.new_value?.status as string | undefined;

    const details = (
      <div className="space-y-1.5 text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
        <p className="font-semibold text-gray-900">{actionText}</p>
        <p>
          نام مراجعه‌کننده: <span className="font-medium text-gray-900">{patientName}</span>
        </p>
        {serviceName && (
          <p>
            خدمت: <span className="font-medium text-gray-900">{serviceName}</span>
          </p>
        )}
        {isAppointmentLog && (
          <p>
            نوبت:{' '}
            <span className="font-medium text-blue-700">#{1000 + log.entity_id!}</span>
          </p>
        )}
        {(kind === 'status_changed' || kind === 'cancelled' || kind === 'completed') && oldStatus && newStatus && (
          <p>
            تغییر وضعیت:{' '}
            <span className="font-medium text-gray-900">
              {statusLabelFa(oldStatus)} ← {statusLabelFa(newStatus)}
            </span>
          </p>
        )}
        {kind === 'time_set' && (
          <p>
            زمان مراجعه:{' '}
            <span className="font-medium text-gray-900">
              {log.new_value?.appointment_date || '-'}
              {log.new_value?.appointment_time ? ` — ${log.new_value.appointment_time}` : ''}
            </span>
          </p>
        )}
        {kind === 'time_set' && log.new_value?.between_patients && (
          <p>
            نوع زمان‌بندی: <span className="font-medium text-gray-900">بین بیماران</span>
          </p>
        )}
        {cancelReason && (
          <p>
            دلیل لغو: <span className="font-medium text-red-700">{cancelReason}</span>
          </p>
        )}
        <p className="text-xs text-gray-500 pt-1">{formatLogDateTime(log.created_at)}</p>
      </div>
    );

    const header = (
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {getActivityIcon(log)}
          <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${activityTypeBadge.className}`}>
            {activityTypeBadge.label}
          </span>
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap">{formatRelativeTime(log.created_at)}</span>
      </div>
    );

    if (!isAppointmentLog) {
      return (
        <div key={log.id} className="p-4 rounded-xl border border-gray-100 bg-white">
          {header}
          {details}
        </div>
      );
    }

    return (
      <button
        key={log.id}
        type="button"
        onClick={() => openAppointmentFromLog(log)}
        className="w-full p-4 rounded-xl border border-gray-100 bg-white hover:border-blue-200 hover:shadow-sm transition-all text-right"
      >
        {header}
        {details}
      </button>
    );
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner text="در حال بارگذاری مانیتورینگ..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {stats && (
          <>
            {/* آمار کلی */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              {(Object.keys(STATUS_CARD_CONFIG) as MonitoringStatusFilter[]).map((filterKey) => {
                const card = STATUS_CARD_CONFIG[filterKey];
                return (
                  <button
                    key={filterKey}
                    type="button"
                    onClick={() => openStatusListModal(filterKey)}
                    className="bg-white rounded-lg shadow p-6 text-right w-full cursor-pointer hover:shadow-md hover:ring-2 hover:ring-blue-100 transition-all border border-transparent hover:border-blue-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{card.title}</p>
                        <p className={`text-3xl font-bold mt-2 ${card.color}`}>
                          {stats[card.valueKey] as number}
                        </p>
                      </div>
                      <div className={`w-12 h-12 ${card.iconBg} rounded-full flex items-center justify-center`}>
                        {card.icon}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* آمار فعالیت کاربران */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">فعالیت کاربران</h2>
                  <div className="w-44" style={{ direction: 'rtl' }}>
                    <PersianDatePicker
                      value={selectedDate}
                      onChange={(newDate) => {
                        setSelectedDate(newDate);
                        selectedDateRef.current = newDate;
                        fetchUserActivityStats(newDate);
                      }}
                      placeholder="انتخاب تاریخ"
                    />
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4 max-h-96 overflow-y-auto" style={{ direction: 'rtl' }}>
                    {userActivityStats.length > 0 ? (
                      userActivityStats.map((userStat, index) => (
                        <div
                          key={userStat.admin_id}
                          onClick={() => fetchUserDailyLogs(userStat.admin_id, userStat.admin_username)}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${index === 0 ? 'bg-yellow-500' :
                              index === 1 ? 'bg-gray-400' :
                                index === 2 ? 'bg-orange-400' :
                                  'bg-blue-400'
                              }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-900">
                                {userStat.admin_username}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {userStat.total_activities} فعالیت • {userStat.unique_appointments} نوبت
                              </p>
                            </div>
                          </div>
                          <div className="text-left">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-blue-100">
                              <span className="text-lg font-bold text-blue-600">{userStat.total_activities}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-8">هنوز فعالیتی ثبت نشده</p>
                    )}
                  </div>
                </div>
              </div>

              {/* آخرین نوبت‌ها */}
              <button
                type="button"
                onClick={openLatestAppointmentsModal}
                className="bg-white rounded-lg shadow text-right w-full cursor-pointer hover:shadow-md hover:ring-2 hover:ring-blue-100 transition-all border border-transparent hover:border-blue-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">آخرین نوبت‌ها</h2>
                  <span className="text-xs text-blue-600 font-medium">مشاهده همه</span>
                </div>
                <div className="p-6">
                  <div
                    className="space-y-3 max-h-96 overflow-y-auto pr-1"
                    style={{ direction: 'rtl' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {stats.latest_appointments && stats.latest_appointments.length > 0 ? (
                      stats.latest_appointments.map((apt) => renderLatestAppointmentItem(apt))
                    ) : (
                      <p className="text-center text-gray-500 py-8">هیچ نوبتی یافت نشد</p>
                    )}
                  </div>
                </div>
              </button>

              {/* لاگ فعالیت‌ها */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">لاگ فعالیت‌ها</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {activityLog.length.toLocaleString('fa-IR')} رویداد
                    </span>
                    <button
                      type="button"
                      onClick={openActivityLogModal}
                      className="text-xs text-blue-600 font-medium hover:text-blue-800"
                    >
                      مشاهده همه
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1" style={{ direction: 'rtl' }}>
                    {activityLog.length > 0 ? (
                      activityLog.map((log) => renderActivityLogItem(log))
                    ) : (
                      <p className="text-center text-gray-500 py-8">هنوز فعالیتی ثبت نشده</p>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </>
        )}
      </div>

      {showStatusListModal && selectedStatusFilter && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ direction: 'rtl' }}
          onClick={closeStatusListModal}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {STATUS_CARD_CONFIG[selectedStatusFilter].title}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {statusListTotal.toLocaleString('fa-IR')} نوبت
                </p>
              </div>
              <button
                type="button"
                onClick={closeStatusListModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {statusListLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner text="در حال دریافت نوبت‌ها..." />
                </div>
              ) : statusListAppointments.length === 0 ? (
                <p className="text-center text-gray-500 py-8">نوبتی در این وضعیت یافت نشد.</p>
              ) : (
                <div className="space-y-2">
                  {statusListAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="flex items-stretch gap-2 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-all"
                    >
                      <button
                        type="button"
                        onClick={() => openAppointmentDetail(apt)}
                        className="flex-1 min-w-0 flex items-center justify-between gap-3 text-right"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-semibold text-gray-900 truncate">
                              {apt.user?.full_name || 'کاربر'}
                            </p>
                            {getStatusBadge(getAppointmentDisplayStatus(apt))}
                          </div>
                          <p className="text-sm text-gray-600 truncate">
                            {apt.service?.name || 'نامشخص'}
                          </p>
                          {apt.cancel_reason?.trim() && (
                            <p className="text-xs text-red-700 mt-1 truncate">
                              دلیل لغو: {apt.cancel_reason.trim()}
                            </p>
                          )}
                          {apt.cancelled_by_username?.trim() && (
                            <p className="text-xs text-gray-600 mt-0.5 truncate">
                              لغو کننده: {apt.cancelled_by_username.trim()}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(apt.created_at).toLocaleString('fa-IR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                        <div className="shrink-0 text-left">
                          <p className="text-sm font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg">
                            #{apt.tracking_code || 1000 + apt.id}
                          </p>
                          {apt.appointment_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              {apt.appointment_date}
                              {apt.appointment_time ? ` — ${apt.appointment_time}` : ''}
                            </p>
                          )}
                        </div>
                      </button>
                      {selectedStatusFilter === 'cancelled' && (
                        <button
                          type="button"
                          onClick={() => openCallHistoryModal(apt)}
                          className="shrink-0 self-center inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
                          title="تاریخچه تماس"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          تاریخچه تماس
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {statusListTotalPages > 1 && !statusListLoading && (
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  disabled={statusListPage <= 1}
                  onClick={() => selectedStatusFilter && fetchStatusAppointments(selectedStatusFilter, statusListPage - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  قبلی
                </button>
                <span className="text-sm text-gray-600">
                  صفحه {statusListPage.toLocaleString('fa-IR')} از {statusListTotalPages.toLocaleString('fa-IR')}
                </span>
                <button
                  type="button"
                  disabled={statusListPage >= statusListTotalPages}
                  onClick={() => selectedStatusFilter && fetchStatusAppointments(selectedStatusFilter, statusListPage + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  بعدی
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showAppointmentDetailModal && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeAppointmentDetailModal}
          style={{ direction: 'ltr' }}
        >
          <div
            className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ direction: 'rtl' }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <h3 className="text-lg font-bold text-gray-900">
                {appointmentDetailLoading
                  ? 'خلاصه نوبت'
                  : `خلاصه نوبت #${selectedAppointment?.tracking_code || (selectedAppointment ? 1000 + selectedAppointment.id : '')}`}
              </h3>
              <button
                type="button"
                onClick={closeAppointmentDetailModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            {appointmentDetailLoading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner text="در حال دریافت جزئیات نوبت..." />
              </div>
            ) : selectedAppointment ? (
              <>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(getAppointmentDisplayStatus(selectedAppointment))}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام و نام خانوادگی</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {selectedAppointment.user?.full_name || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">شماره تماس</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md" dir="ltr">
                    {selectedAppointment.user?.phone || '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">خدمت</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {selectedAppointment.service?.name || 'نامشخص'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ مراجعه</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {selectedAppointment.appointment_date || '-'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ساعت مراجعه</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {selectedAppointment.appointment_time || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاریخ ایجاد</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {new Date(selectedAppointment.created_at).toLocaleString('fa-IR', {
                      timeZone: 'Asia/Tehran',
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>

              {selectedAppointment.cancel_reason?.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">دلیل لغو</label>
                  <p className="text-sm text-red-800 bg-red-50 px-3 py-2 rounded-md">
                    {selectedAppointment.cancel_reason.trim()}
                  </p>
                </div>
              )}

              {selectedAppointment.cancelled_by_username?.trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">لغو کننده</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {selectedAppointment.cancelled_by_username.trim()}
                  </p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex justify-end gap-3 rounded-b-xl">
              <button
                type="button"
                onClick={closeAppointmentDetailModal}
                className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                بستن
              </button>
              <button
                type="button"
                onClick={() => handleViewFullAppointment(selectedAppointment.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                مشاهده جزئیات کامل
              </button>
            </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {showLatestAppointmentsModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ direction: 'rtl' }}
          onClick={closeLatestAppointmentsModal}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">آخرین نوبت‌ها</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {(stats?.total_appointments ?? 0).toLocaleString('fa-IR')} نوبت
                </p>
              </div>
              <button
                type="button"
                onClick={closeLatestAppointmentsModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {latestAppointmentsLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner text="در حال دریافت نوبت‌ها..." />
                </div>
              ) : latestAppointmentsList.length === 0 ? (
                <p className="text-center text-gray-500 py-8">هیچ نوبتی یافت نشد.</p>
              ) : (
                <div className="space-y-2">
                  {latestAppointmentsList.map((apt) => renderLatestAppointmentItem(apt))}
                </div>
              )}
            </div>

            {latestAppointmentsTotalPages > 1 && !latestAppointmentsLoading && (
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  disabled={latestAppointmentsPage <= 1}
                  onClick={() => fetchLatestAppointmentsModal(latestAppointmentsPage - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  قبلی
                </button>
                <span className="text-sm text-gray-600">
                  صفحه {latestAppointmentsPage.toLocaleString('fa-IR')} از{' '}
                  {latestAppointmentsTotalPages.toLocaleString('fa-IR')}
                </span>
                <button
                  type="button"
                  disabled={latestAppointmentsPage >= latestAppointmentsTotalPages}
                  onClick={() => fetchLatestAppointmentsModal(latestAppointmentsPage + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  بعدی
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showActivityLogModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ direction: 'rtl' }}
          onClick={closeActivityLogModal}
        >
          <div
            className="bg-white w-full max-w-3xl rounded-xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">لاگ فعالیت‌ها</h3>
                <p className="text-sm text-gray-500 mt-0.5">تاریخچه فعالیت‌های سیستم</p>
              </div>
              <button
                type="button"
                onClick={closeActivityLogModal}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">
              {activityLogModalLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner text="در حال دریافت فعالیت‌ها..." />
                </div>
              ) : activityLogModalList.length === 0 ? (
                <p className="text-center text-gray-500 py-8">هنوز فعالیتی ثبت نشده.</p>
              ) : (
                <div className="space-y-2">
                  {activityLogModalList.map((log) => renderActivityLogItem(log))}
                </div>
              )}
            </div>

            {(activityLogModalPage > 1 || activityLogModalHasMore) && !activityLogModalLoading && (
              <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between shrink-0">
                <button
                  type="button"
                  disabled={activityLogModalPage <= 1}
                  onClick={() => fetchActivityLogsModal(activityLogModalPage - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  قبلی
                </button>
                <span className="text-sm text-gray-600">
                  صفحه {activityLogModalPage.toLocaleString('fa-IR')}
                </span>
                <button
                  type="button"
                  disabled={!activityLogModalHasMore}
                  onClick={() => fetchActivityLogsModal(activityLogModalPage + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                >
                  بعدی
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showUserLogsModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" style={{ direction: 'rtl' }}>
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900">ریز فعالیت روزانه</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {selectedUserName} • {selectedDate}
                  {!userLogsLoading && selectedUserLogs.length > 0 && (
                    <> • {selectedUserLogs.length.toLocaleString('fa-IR')} فعالیت</>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowUserLogsModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                aria-label="بستن"
              >
                ×
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {userLogsLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner text="در حال دریافت فعالیت‌ها..." />
                </div>
              ) : selectedUserLogs.length === 0 ? (
                <p className="text-center text-gray-500 py-8">برای این کاربر در این روز فعالیتی ثبت نشده است.</p>
              ) : (
                <div className="space-y-3">
                  {selectedUserLogs.map((log) => renderUserDailyLogItem(log))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <AppointmentCallHistoryModal
        isOpen={showCallHistoryModal}
        onClose={closeCallHistoryModal}
        appointmentId={callHistoryAppointment?.id ?? null}
        appointmentLabel={
          callHistoryAppointment
            ? `نوبت #${callHistoryAppointment.tracking_code || 1000 + callHistoryAppointment.id}${
                callHistoryAppointment.user?.full_name
                  ? ` — ${callHistoryAppointment.user.full_name}`
                  : ''
              }`
            : undefined
        }
      />
    </div>
  );
};

export default AdminMonitoring;

